import React, { createContext, useState, useContext, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';

import { DB } from '~/services/firebase';
import ICoupon from '~/interfaces/ICoupon';
import IUsedCoupons from '~/interfaces/IUsedCoupons';

interface CouponsContextData {
  coupons: ICoupon[];
  loadCoupons(id: string): Promise<void>;
  clearContext(): void;
  isInitialized: boolean;
  findUsedCoupons: (props: FindUsedCouponsProps) => Promise<IUsedCoupons[]>;
}

type FindUsedCouponsProps = {
  company_id: string;
  coupon_id: string;
  phone: string;
};

const CouponsContext = createContext<CouponsContextData>(
  {} as CouponsContextData,
);

export const CouponsProvider: React.FC = ({ children }) => {
  const [coupons, setCoupons] = useState<ICoupon[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const clearContext = useCallback(() => {
    setCoupons([]);
    setIsInitialized(false);
  }, []);

  const loadCoupons = useCallback(async (id: string) => {
    const q = query(collection(DB, 'coupons'), where('company_id', '==', id));
    const querySnapshot = await getDocs(q);

    const items: ICoupon[] = [];

    querySnapshot.docs.forEach(item => {
      const parseItem = {
        ...item.data(),
        uid: item.id,
      } as ICoupon;

      items.push(parseItem);
    });

    setCoupons(items);
    setIsInitialized(true);
  }, []);

  const findUsedCoupons = useCallback(async (props: FindUsedCouponsProps) => {
    const q = query(
      collection(DB, 'used_coupons'),
      where('company_id', '==', props.company_id),
      where('coupon_id', '==', props.coupon_id),
      where('phone', '==', props.phone),
    );
    const querySnapshot = await getDocs(q);

    const items: IUsedCoupons[] = [];

    querySnapshot.docs.forEach(item => {
      const parseItem = {
        ...item.data(),
        uid: item.id,
      } as IUsedCoupons;

      items.push(parseItem);
    });

    return items;
  }, []);

  return (
    <CouponsContext.Provider
      value={{
        coupons,
        loadCoupons,
        clearContext,
        isInitialized,
        findUsedCoupons,
      }}
    >
      {children}
    </CouponsContext.Provider>
  );
};

export function useCoupons(): CouponsContextData {
  const context = useContext(CouponsContext);
  return context;
}
